import { Suspense, useRef, useState, useEffect } from 'react'
import { Canvas, useThree } from '@react-three/fiber'
import { 
  OrbitControls, 
  Grid, 
  Environment, 
  Center,
  Html,
  useProgress,
  PerspectiveCamera,
} from '@react-three/drei'
import { STLLoader, OBJLoader } from 'three-stdlib'
import * as THREE from 'three'
import { motion } from 'framer-motion'
import { 
  RotateCcw, 
  Maximize2, 
  Grid3X3,
  Sun,
  Loader2,
  AlertCircle,
} from 'lucide-react'

interface ModelViewerProps {
  fileUrl?: string
  file?: File
  onModelLoad?: (info: ModelInfo) => void
  className?: string
  showControls?: boolean
  autoRotate?: boolean
}

export interface ModelInfo {
  vertices: number
  triangles: number
  boundingBox: {
    width: number
    height: number
    depth: number
  }
  volume: number
  surfaceArea: number
  isManifold: boolean
  center: THREE.Vector3
}

function Loader() {
  const { progress } = useProgress()
  return (
    <Html center>
      <div className="flex flex-col items-center gap-2">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
        <span className="text-sm text-surface-400">{progress.toFixed(0)}%</span>
      </div>
    </Html>
  )
}

function Model({ 
  fileUrl, 
  file, 
  onLoad 
}: { 
  fileUrl?: string
  file?: File
  onLoad?: (info: ModelInfo) => void 
}) {
  const [geometry, setGeometry] = useState<THREE.BufferGeometry | null>(null)
  const [error, setError] = useState<string | null>(null)
  const meshRef = useRef<THREE.Mesh>(null)
  const { camera } = useThree()

  useEffect(() => {
    const loadModel = async () => {
      try {
        let data: ArrayBuffer
        let fileName: string
        
        if (file) {
          data = await file.arrayBuffer()
          fileName = file.name.toLowerCase()
        } else if (fileUrl) {
          const response = await fetch(fileUrl)
          data = await response.arrayBuffer()
          fileName = fileUrl.toLowerCase()
        } else {
          return
        }

        let loadedGeometry: THREE.BufferGeometry

        if (fileName.endsWith('.stl')) {
          const loader = new STLLoader()
          loadedGeometry = loader.parse(data)
        } else if (fileName.endsWith('.obj')) {
          const loader = new OBJLoader()
          const text = new TextDecoder().decode(data)
          const group = loader.parse(text)
          const mesh = group.children.find((c): c is THREE.Mesh => c instanceof THREE.Mesh)
          if (mesh) {
            loadedGeometry = mesh.geometry as THREE.BufferGeometry
          } else {
            throw new Error('No mesh found in OBJ file')
          }
        } else {
          throw new Error('Unsupported file format')
        }

        loadedGeometry.computeVertexNormals()
        loadedGeometry.center()
        loadedGeometry.computeBoundingBox()

        const box = loadedGeometry.boundingBox!
        const size = new THREE.Vector3()
        box.getSize(size)
        const maxDim = Math.max(size.x, size.y, size.z)
        const scale = 100 / maxDim
        loadedGeometry.scale(scale, scale, scale)
        loadedGeometry.computeBoundingBox()

        setGeometry(loadedGeometry)

        if (onLoad) {
          const info = analyzeGeometry(loadedGeometry)
          onLoad(info)
        }

        const newBox = loadedGeometry.boundingBox!
        const newSize = new THREE.Vector3()
        newBox.getSize(newSize)
        const maxNewDim = Math.max(newSize.x, newSize.y, newSize.z)
        ;(camera as THREE.PerspectiveCamera).position.set(
          maxNewDim * 1.5,
          maxNewDim * 1.5,
          maxNewDim * 1.5
        )
        camera.lookAt(0, 0, 0)

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load model')
      }
    }

    loadModel()
  }, [fileUrl, file, camera, onLoad])

  if (error) {
    return (
      <Html center>
        <div className="flex flex-col items-center gap-2 text-red-400">
          <AlertCircle className="w-8 h-8" />
          <span className="text-sm">{error}</span>
        </div>
      </Html>
    )
  }

  if (!geometry) return null

  return (
    <Center>
      <mesh ref={meshRef} geometry={geometry} castShadow receiveShadow>
        <meshPhysicalMaterial
          color="#8b5cf6"
          metalness={0.1}
          roughness={0.4}
          clearcoat={0.3}
          clearcoatRoughness={0.25}
          envMapIntensity={0.5}
        />
      </mesh>
    </Center>
  )
}

function analyzeGeometry(geometry: THREE.BufferGeometry): ModelInfo {
  const position = geometry.getAttribute('position')
  const vertices = position.count
  const triangles = geometry.index ? geometry.index.count / 3 : vertices / 3

  geometry.computeBoundingBox()
  const box = geometry.boundingBox!
  const size = new THREE.Vector3()
  box.getSize(size)

  let volume = 0
  let surfaceArea = 0
  const posArray = position.array

  for (let i = 0; i < vertices; i += 3) {
    const v1 = new THREE.Vector3(posArray[i * 3], posArray[i * 3 + 1], posArray[i * 3 + 2])
    const v2 = new THREE.Vector3(posArray[(i + 1) * 3], posArray[(i + 1) * 3 + 1], posArray[(i + 1) * 3 + 2])
    const v3 = new THREE.Vector3(posArray[(i + 2) * 3], posArray[(i + 2) * 3 + 1], posArray[(i + 2) * 3 + 2])

    const cross = new THREE.Vector3().crossVectors(
      new THREE.Vector3().subVectors(v2, v1),
      new THREE.Vector3().subVectors(v3, v1)
    )
    surfaceArea += cross.length() / 2

    volume += v1.dot(cross) / 6
  }

  volume = Math.abs(volume)

  const center = new THREE.Vector3()
  box.getCenter(center)

  return {
    vertices,
    triangles: Math.floor(triangles),
    boundingBox: {
      width: parseFloat(size.x.toFixed(2)),
      height: parseFloat(size.y.toFixed(2)),
      depth: parseFloat(size.z.toFixed(2)),
    },
    volume: parseFloat(volume.toFixed(2)),
    surfaceArea: parseFloat(surfaceArea.toFixed(2)),
    isManifold: volume > 0,
    center,
  }
}

function Scene({ 
  fileUrl, 
  file, 
  onModelLoad, 
  showGrid = true,
  autoRotate = false,
}: { 
  fileUrl?: string
  file?: File
  onModelLoad?: (info: ModelInfo) => void
  showGrid?: boolean
  autoRotate?: boolean
}) {
  return (
    <>
      <PerspectiveCamera makeDefault position={[150, 150, 150]} fov={45} />
      <ambientLight intensity={0.4} />
      <directionalLight
        position={[100, 100, 50]}
        intensity={1}
        castShadow
        shadow-mapSize={[2048, 2048]}
      />
      <directionalLight position={[-100, 50, -50]} intensity={0.5} />
      <pointLight position={[0, 100, 0]} intensity={0.3} />
      
      <Suspense fallback={<Loader />}>
        <Model fileUrl={fileUrl} file={file} onLoad={onModelLoad} />
        <Environment preset="studio" />
      </Suspense>
      
      {showGrid && (
        <Grid
          args={[200, 200]}
          cellSize={10}
          cellThickness={0.5}
          cellColor="#374151"
          sectionSize={50}
          sectionThickness={1}
          sectionColor="#4b5563"
          fadeDistance={400}
          fadeStrength={1}
          followCamera={false}
          position={[0, -50, 0]}
        />
      )}
      
      <OrbitControls
        makeDefault
        enableDamping
        dampingFactor={0.05}
        minDistance={50}
        maxDistance={500}
        autoRotate={autoRotate}
        autoRotateSpeed={1}
      />
    </>
  )
}

export default function ModelViewer({
  fileUrl,
  file,
  onModelLoad,
  className = '',
  showControls = true,
  autoRotate = false,
}: ModelViewerProps) {
  const [showGrid, setShowGrid] = useState(true)
  const [isAutoRotate, setIsAutoRotate] = useState(autoRotate)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const handleReset = () => {
    // Reset camera position - handled by OrbitControls
  }

  const handleFullscreen = () => {
    if (canvasRef.current?.parentElement) {
      canvasRef.current.parentElement.requestFullscreen?.()
    }
  }

  return (
    <div className={`relative bg-surface-900 rounded-xl overflow-hidden ${className}`}>
      <Canvas
        ref={canvasRef}
        shadows
        gl={{ 
          antialias: true, 
          alpha: true,
          preserveDrawingBuffer: true,
        }}
        dpr={[1, 2]}
        className="!bg-gradient-to-b from-surface-800 to-surface-900"
      >
        <Scene 
          fileUrl={fileUrl} 
          file={file} 
          onModelLoad={onModelLoad}
          showGrid={showGrid}
          autoRotate={isAutoRotate}
        />
      </Canvas>

      {showControls && (
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 p-1 bg-surface-900/90 backdrop-blur-sm rounded-lg border border-surface-700"
        >
          <button
            onClick={handleReset}
            className="p-2 rounded-md hover:bg-surface-700 text-surface-400 hover:text-white transition-colors"
            title="Reset View"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowGrid(!showGrid)}
            className={`p-2 rounded-md transition-colors ${
              showGrid ? 'bg-primary-500/20 text-primary-400' : 'hover:bg-surface-700 text-surface-400 hover:text-white'
            }`}
            title="Toggle Grid"
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsAutoRotate(!isAutoRotate)}
            className={`p-2 rounded-md transition-colors ${
              isAutoRotate ? 'bg-primary-500/20 text-primary-400' : 'hover:bg-surface-700 text-surface-400 hover:text-white'
            }`}
            title="Auto Rotate"
          >
            <Sun className="w-4 h-4" />
          </button>
          <div className="w-px h-6 bg-surface-700 mx-1" />
          <button
            onClick={handleFullscreen}
            className="p-2 rounded-md hover:bg-surface-700 text-surface-400 hover:text-white transition-colors"
            title="Fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </motion.div>
      )}
    </div>
  )
}
